import { useCreateMultipleSample } from "@/api/multiplesample";
import CopyFromMasterField from "@/components/form-items/CopyFromMasterField";
import { zodResolver } from "@hookform/resolvers/zod";
import { isAxiosError } from "axios";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import NewItemDialog from "../../modal/NewItemDialog";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../ui/form";
import { Input } from "../../ui/input";

const schema = z.object({
  exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement: z
    .string()
    .min(1, "required"),
});

type FormType = z.infer<typeof schema>;

type NewExemplaireDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  multipleprintId: string;
  isFirstChild?: boolean;
  onCreateExemplaire: (exemplaire: {
    id: string;
    exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement?: string;
  }) => void;
};

const NewExemplaireDialog = ({
  onOpenChange,
  open,
  multipleprintId,
  isFirstChild = false,
  onCreateExemplaire,
}: NewExemplaireDialogProps) => {
  const { t } = useTranslation();
  const [copyFromMaster, setCopyFromMaster] = useState(false);
  const form = useForm<FormType>({
    reValidateMode: "onSubmit",
    resolver: zodResolver(schema),
    defaultValues: {
      exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement: "",
    },
  });
  const { mutateAsync } = useCreateMultipleSample();

  const onFormSubmit = async (data: FormType) => {
    try {
      const created = await mutateAsync({
        exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement:
          data.exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement,
        multipleprints: { connect: { id: multipleprintId } },
        copyFromMaster: isFirstChild && copyFromMaster,
      });
      onCreateExemplaire({
        id: created.id,
        exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement:
          created.exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement,
      });
      onOpenChange(false);
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        form.setError(
          "exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement",
          {
            message: t("itemWithSameIdAlreadyExists"),
          },
        );
        return;
      }
      form.setError("root", { message: t("unknownError") });
      throw err;
    }
  };

  return (
    <NewItemDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("addExemplaire")}
      onFormSubmit={onFormSubmit}
      form={form}
    >
      <FormField
        control={form.control}
        name="exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement"
        render={({ field }) => (
          <FormItem>
            <FormLabel required>
              {t(
                "exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement",
              )}
            </FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {isFirstChild ? (
        <CopyFromMasterField
          checked={copyFromMaster}
          onCheckedChange={setCopyFromMaster}
        />
      ) : null}
      <FormMessage>{form.formState.errors.root?.message}</FormMessage>
    </NewItemDialog>
  );
};

export default NewExemplaireDialog;
